<script setup>
    import { ref } from "vue";

    const map=ref(null);
    const popup=ref(null);
    const popUpDataTable=ref(null);
    const popupCloser=ref(null);
    
</script>

<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
    <div v-else class="container py-5">
        <h2>{{ $t('datasetDetail') }}</h2>
        <nav ref="breadcrumb" aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ data.name }}</li>
            </ol>
        </nav>
        <div class="row">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
            <div class="mb-3 col-md-8">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
        </div>
        <div v-if="renderMap && content.center" class="mb-3 overflow-auto">
            <nav>
                <div class="nav nav-tabs" id="nav-tab" role="tablist">
                    <button class="nav-link active" id="map-tab" data-bs-toggle="tab" data-bs-target="#map" type="button" role="tab" aria-controls="map" aria-selected="true">{{ $t('map')}}</button>
                    <button class="nav-link" @click="adjustDt()" id="data-tab" data-bs-toggle="tab" data-bs-target="#data" type="button" role="tab" aria-controls="data" aria-selected="false">{{$t('data')}}</button>
                </div>
            </nav>
            <div class="tab-content" id="nav-tabContent">
                <div class="tab-pane fade show active" id="map" role="tabpanel" aria-labelledby="map-tab">
                    <div v-if="mapLoading" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); padding: 10px;">
                        <span class="spinner-border" role="status" aria-hidden="true"></span>
                    </div>
                    <div ref="map" class="map"></div>
                    <div style="display: none">
                        <div id="popup" class="ol-popup" ref="popup">
                            <div class="btn-group btn-group-toggle" data-toggle="buttons" style="width:100%">
                            </div>
                            <a href="#" id="popup-closer" class="ol-popup-closer" ref="popupCloser"></a>
                            <div id="popup-content">
                                <table class="display compact responsive nowrap" width="100%" ref="popUpDataTable">
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="tab-pane fade" id="data" role="tabpanel" aria-labelledby="data-tab">
                    <DataTable
                        ref="dt"
                        :pageLength="5"
                        :lengthMenu="[ 5, 25, 50, 75, 100 ]"
                        :columns="tableColumns"
                        :options="tableOptions"
                        :data="tableData"
                        class="display"
                    />
                </div>
            </div>
        </div>
        <div v-else class="mb-3 overflow-auto">
            <DataTable
                ref="dt"
                :pageLength="5"
                :lengthMenu="[ 5, 25, 50, 75, 100 ]"
                :columns="tableColumns"
                :options="tableOptions"
                :data="tableData"
                class="display"
            />
        </div>
        <div class="d-grid gap-2 d-md-flex ">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
                <button type="button" class="btn btn-danger" @click="showDeleteDataset">{{ $t('delete') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}` })">{{ $t('back') }}</button>
            <div class="ms-auto" v-if="isUserAdmin">
                <button class="btn btn-secondary me-2" @click="datasetZipDownload">{{ $t('downloadZip') }}</button>
                <button class="btn btn-secondary" @click="showImportXlsxModal = true">{{ $t('Upload') }}</button>
            </div>
        </div>
        <DatasetDeleteModal 
            v-if="showDatasetDeleteModal"
            @reload="$router.go(-1)"
            @closeDeleteDialog="showDatasetDeleteModal = false"
            :dataset-id="data.id"
            :modeling-id="$route.params.id"
        />
        <DatasetXlsxUploadDialog
            v-if="showImportXlsxModal" 
            @reload="$router.go(0)" 
            @close="showImportXlsxModal = false" 
            :dataset-id="data.id"
            :modeling-id="$route.params.id"
        />
    </div>
</template>

<script>
import { ref, nextTick } from 'vue';
import DatasetApi from "../../api/server/dataset";
import ModelingApi from "../../api/server/modeling";
import DataTable from 'datatables.net-vue3';
import DataTablesLib from 'datatables.net';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import Map from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { getFeaturesFromSections, defaultLineStyleFunction } from "../../utils/feature-ext";
import { Select } from 'ol/interaction';
import { parseCoords } from "../../utils/geom-coords";
import 'ol/ol.css';
import 'ol-layerswitcher/dist/ol-layerswitcher.css';
import DatasetDeleteModal from "./DatasetDeleteModal.vue"
import DatasetXlsxUploadDialog from "./DatasetXlsxUploadDialog.vue"
import { CheckIfUserIsEditor } from "../../authCache";

DataTable.use(DataTablesLib);

export default {
    name: "datasetDetail",
    components: {
        DatasetDeleteModal,
        DatasetXlsxUploadDialog,
        DataTable
    },
    mounted() {
        this.requestData();
    },
    methods: {
        datasetZipDownload() {
            var request = DatasetApi.datasetZipDownload(this.$route.params.id, this.$route.params.datasetId);
            window.open(request.url);
        },
        showDeleteDataset() {
            this.datasetToDeleteId = this.data.id;
            this.showDatasetDeleteModal = true;
        },
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsEditor();

            this.data = await DatasetApi.datasetDetail(this.$route.params.id, this.$route.params.datasetId);
            const modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            if(this.data.content) {
                this.content = JSON.parse(this.data.content);
                this.tableColumns = [];
                if(this.content.headers) 
                    this.tableColumns = this.content.headers.map(h => ({ data: h.name, title: this.$t(h.alias), defaultContent: "", width: 100 }));
            }

            this.sections = await DatasetApi.datasetSections(this.$route.params.id, this.$route.params.datasetId);
            // if sections contain type - we render map
            this.renderMap = this.sections[0]?.["type"] != null;
            this.tableData = this.sections.map(s => s["properties"]);
            
            this.pending = false;
            if(this.renderMap && this.content.center) {
                await nextTick();
                this.adjustDt();
                this.initMap();
            }
        },
        async onSubmit() {
            try {
                if(this.data.name.length > 0) {
                    this.data.content = JSON.stringify(this.content);
                    await DatasetApi.datasetUpdate(this.$route.params.id, this.data);
                
                    this.showSuccess();
                } else {
                    this.showEmptyNameError();
                }
            } catch (error) {
                console.error("Error updating data:", error);
            }
        },
        async initMap() {
            var self = this;
            self.mapLoading = true;

            if (!self.$refs.map) {
                return;
            }

            // Release previous map
            if (self.map) {
                self.map.setTarget(undefined);
            }

            if (self.popUpDataTable) {
                self.popUpDataTable.destroy(true);
            }

            const layers = [
                new TileLayer({ source: new OSM() }),
            ];

            const overlay = new Overlay({
                element: self.$refs.popup,
                autoPan: {
                    animation: {
                        duration: 250,
                    },
                },
            });

            if (self.$refs["popupCloser"]) {
                self.$refs["popupCloser"].onclick = () => {
                    overlay.setPosition(undefined);
                    self.$refs["popupCloser"].blur();
                    return false;
                };
            }
            var projection = 'GEOGCS["WGS 84", DATUM["World Geodetic System 1984", SPHEROID["WGS 84", 6378137.0, 298.257223563, AUTHORITY["EPSG","7030"]], AUTHORITY["EPSG","6326"]], PRIMEM["Greenwich", 0.0, AUTHORITY["EPSG","8901"]], UNIT["degree", 0.017453292519943295], AXIS["Geodetic longitude", EAST], AXIS["Geodetic latitude", NORTH], AUTHORITY["EPSG","4326"]]'
            const features = getFeaturesFromSections(self.sections, projection);
            if (features.length > 0) {
                const vector = new VectorLayer({ 
                    source: new VectorSource({ features: features }),
                    style: (feature, resolution) => defaultLineStyleFunction(feature, resolution, "#000000", 4)
                });
                layers.push(vector);
            }

            const view = new View({ center: parseCoords(this.content.center), zoom: 12});
            var map = new Map({
                target: self.$refs.map,
                layers: layers,
                overlays: [overlay],
                view: view,
            });


            const select = new Select({
                style: function (feature, resolution) {
                    return defaultLineStyleFunction(feature, resolution, "#A020F0", 4);

                },
            });

            select.on('select', (evt) => {
                if (evt.selected && evt.selected.length > 0) {
                    self.selectedFeatures = evt.target.getFeatures().getArray();
                    self.selectedFeatureIndex = self.selectedFeatures.length;

                    self.setPopupTableData();
                    overlay.setPosition(evt.mapBrowserEvent.coordinate);
                } else {
                    self.selectedFeatures = [];
                    self.selectedFeatureIndex = 0;
                    overlay.setPosition(undefined);
                    self.$refs.popupCloser.blur();
                }
            });

            map.addInteraction(select);
            self.mapLoading = false;
        },
        destroyMap() {
            if (this.map) {
                this.map.setTarget(undefined);
            }
        },
        adjustDt() {
            const api = this.$refs.dt?.dt;
            if (!api) return;
            api.columns.adjust();
        },
        setPopupTableData() {
            var self = this;
            new DataTablesLib(self.$refs["popUpDataTable"]).destroy();
            var currentSelectedFeature = self.selectedFeatures[self.selectedFeatureIndex - 1];
            var currentSelectedFeatureProperties = currentSelectedFeature.get("properties");

            var tmpDataTable = [];
            for(var property in currentSelectedFeatureProperties) {
                tmpDataTable.push({ name: property, value: currentSelectedFeatureProperties[property] });
            }

            self.popUpDataTable = new DataTablesLib(self.$refs["popUpDataTable"], {
                scrollY: '300px',
                scrollCollapse: true,
                paging: false,
                searching: false,
                info: false,
                columns: [
                    {
                        "data": "name",
                        "title": this.$t('attribute')
                    },
                    {
                        "data": "value",
                        "title": this.$t('value')
                    }
                ],
            }).clear().rows.add(tmpDataTable).draw();
        }
    },
    notifications: {
        showSuccess: {
            title: 'success',
            message: "datasetUpdateSucessfull",
            type: 'success'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            map: null,
            popUpDataTable: null,
            tableColumns: [],
            tableData: [],
            tableOptions: {
                fixedHeader: {
                    header: false,
                    footer: true
                },
                scrollX: true,
                scrollY: 350,
                order: [0, 'desc']
            },
            data: {
                name: "",
                id: 0,
                description: ""
            },
            modelingName: null,
            pending: true,
            mapLoading: true,
            selectedFeatures: [],
            selectedFeatureIndex: 0,
            sections: [],
            select: null,
            datasetToDeleteId: 0,
            showDatasetDeleteModal: false,
            showImportXlsxModal: false,
            isUserAdmin: false,
            content: {},
            renderMap: false
        };
    },
};
</script>